import { GatePassDateTimeFields } from "@/components/sales/GatePassDateTimeFields";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { nowDeliveryParts } from "@/lib/sales/gatePass";
import { Ticket } from "lucide-react";

type GatePassOptionCardProps = {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  date: string;
  time: string;
  onDateChange: (next: string) => void;
  onTimeChange: (next: string) => void;
  disabled?: boolean;
  className?: string;
};

/**
 * Gate Pass option card styled like the Remaining Balance / Full Amount cards
 * on the create-sale form. Reuses the same date/time fields as the invoice page.
 */
export function GatePassOptionCard({
  enabled,
  onEnabledChange,
  date,
  time,
  onDateChange,
  onTimeChange,
  disabled = false,
  className,
}: GatePassOptionCardProps) {
  return (
    <div
      className={cn(
        "col-span-full mt-2 rounded-xl border p-4 shadow-sm backdrop-blur-sm",
        enabled
          ? "border-sky-500/30 bg-gradient-to-r from-sky-500/10 via-sky-500/[0.04] to-sky-500/10"
          : "border-border/50 bg-gradient-to-r from-muted/30 via-muted/10 to-muted/30",
        className,
      )}
    >
      <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
        <label
          className={cn(
            "flex items-center gap-3",
            disabled ? "cursor-not-allowed opacity-80" : "cursor-pointer",
          )}
        >
          <div
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border",
              enabled
                ? "border-sky-500/30 bg-sky-500/20 text-sky-600 dark:text-sky-400"
                : "border-border/50 bg-muted/40 text-muted-foreground",
            )}
          >
            <Ticket className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={enabled}
                disabled={disabled}
                onCheckedChange={(checked) => {
                  const next = checked === true;
                  onEnabledChange(next);
                  if (next && (!date.trim() || !time.trim())) {
                    const parts = nowDeliveryParts();
                    onDateChange(parts.date);
                    onTimeChange(parts.time);
                  }
                }}
              />
              <p
                className={cn(
                  "text-xs font-bold uppercase tracking-wider",
                  enabled ? "text-sky-700 dark:text-sky-400" : "text-muted-foreground",
                )}
              >
                Gate Pass
              </p>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {enabled
                ? "Delivery date & time will be saved with this sale"
                : "Optional — issue a gate pass when delivering the vehicle"}
            </p>
          </div>
        </label>
        <span
          className={cn(
            "inline-flex self-start rounded-full px-2.5 py-1 text-[11px] font-medium sm:self-auto",
            enabled
              ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
              : "bg-muted text-muted-foreground",
          )}
        >
          {enabled ? "Delivered" : "—"}
        </span>
      </div>

      {enabled ? (
        <div className="mt-3 border-t border-border/40 pt-3">
          <GatePassDateTimeFields
            date={date}
            time={time}
            onDateChange={onDateChange}
            onTimeChange={onTimeChange}
            disabled={disabled}
            timeId="create-sale-gate-pass-time"
          />
        </div>
      ) : null}
    </div>
  );
}
